import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useMusicPlayer } from "@/context/MusicPlayerContext";
import { useI18n } from "@/context/I18n";
import { Search, Music2, X, User, Disc3 } from "lucide-react";

export default function GlobalSearch() {
  const [q, setQ] = useState("");
  const [tracks, setTracks] = useState([]);
  const [allArtists, setAllArtists] = useState([]);
  const [allAlbums, setAllAlbums] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const { playQueue } = useMusicPlayer();
  const { t } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/music/artists").then((r) => setAllArtists(r.data.artists)).catch(() => {});
    api.get("/music/albums").then((r) => setAllAlbums(r.data.albums)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!q.trim()) { setTracks([]); return; }
    const t2 = setTimeout(() => {
      api.get("/music/tracks", { params: { q } })
        .then((r) => setTracks(r.data.tracks.slice(0, 6)))
        .catch(() => setTracks([]));
    }, 250);
    return () => clearTimeout(t2);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const ql = q.trim().toLowerCase();
  const artists = ql ? allArtists.filter((a) => a.name.toLowerCase().includes(ql)).slice(0, 4) : [];
  const albums = ql ? allAlbums.filter((a) => (a.name + " " + a.artist).toLowerCase().includes(ql)).slice(0, 4) : [];

  const go = (path) => { navigate(path); setOpen(false); setQ(""); };
  const pickTrack = (i) => { playQueue(tracks, i); setOpen(false); };

  const hasResults = tracks.length || artists.length || albums.length;

  return (
    <div ref={boxRef} className="relative w-full max-w-md">
      <div className="flex items-center gap-2 bg-white/10 hover:bg-white/15 focus-within:bg-white/15 rounded-full px-4">
        <Search className="w-4 h-4 text-zinc-400 shrink-0" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={t("search_library_ph")}
          className="flex-1 bg-transparent py-2.5 text-sm text-white placeholder-zinc-400 focus:outline-none"
        />
        {q && (
          <button data-testid="global-search-clear" onClick={() => { setQ(""); setTracks([]); }} className="text-zinc-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {open && q.trim() && (
        <div data-testid="global-search-results" className="absolute top-12 left-0 right-0 z-50 bg-[#1c1c22] border border-white/10 rounded-xl shadow-2xl p-1.5 max-h-[70vh] overflow-y-auto">
          {!hasResults ? (
            <p className="text-sm text-zinc-500 px-3 py-4 text-center">{t("no_results")}</p>
          ) : (
            <>
              {artists.length > 0 && (
                <>
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 px-3 pt-2 pb-1">{t("artists")}</p>
                  {artists.map((a) => (
                    <button key={a.name} data-testid={`global-artist-${a.name}`} onClick={() => go(`/app/artist/${encodeURIComponent(a.name)}`)}
                      className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/10 text-left">
                      {a.thumbnail ? <img src={a.thumbnail} className="w-9 h-9 rounded-full object-cover" alt="" /> : <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center"><User className="w-4 h-4 text-zinc-500" /></div>}
                      <span className="text-sm truncate">{a.name}</span>
                    </button>
                  ))}
                </>
              )}
              {albums.length > 0 && (
                <>
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 px-3 pt-2 pb-1">{t("albums")}</p>
                  {albums.map((al) => (
                    <button key={al.id} data-testid={`global-album-${al.id}`} onClick={() => go(`/app/album/${al.id}`)}
                      className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/10 text-left">
                      {al.cover ? <img src={al.cover} className="w-9 h-9 rounded object-cover" alt="" /> : <div className="w-9 h-9 rounded bg-white/10 flex items-center justify-center"><Disc3 className="w-4 h-4 text-zinc-500" /></div>}
                      <div className="min-w-0"><p className="text-sm truncate">{al.name}</p><p className="text-xs text-zinc-400 truncate">{al.artist}</p></div>
                    </button>
                  ))}
                </>
              )}
              {tracks.length > 0 && (
                <>
                  <p className="text-[11px] uppercase tracking-wider text-zinc-500 px-3 pt-2 pb-1">{t("songs")}</p>
                  {tracks.map((tr, i) => (
                    <button key={tr.id} data-testid={`global-result-${tr.id}`} onClick={() => pickTrack(i)}
                      className="w-full flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-white/10 text-left">
                      {tr.thumbnail ? <img src={tr.thumbnail} className="w-9 h-9 rounded object-cover" alt="" /> : <div className="w-9 h-9 rounded bg-white/10 flex items-center justify-center"><Music2 className="w-4 h-4 text-zinc-500" /></div>}
                      <div className="min-w-0"><p className="text-sm truncate">{tr.title}</p><p className="text-xs text-zinc-400 truncate">{tr.artist}</p></div>
                    </button>
                  ))}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
